import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { MessageSquareIcon, XIcon, SendIcon } from 'lucide-react';
import { submitLead, validateEmail } from '../lib/leads';
import { apiPost } from '../lib/apiClient';
import { LIVE_API, ENDPOINTS } from '../lib/config';
import type { ServiceLineId } from '../data/services';
interface Message {
  id: number;
  from: 'concierge' | 'visitor';
  text: string;
  routeTo?: ServiceLineId | 'call';
}
const ROUTES: {
  line: ServiceLineId;
  keywords: string[];
  reply: string;
}[] = [{
  line: 'intelligence',
  keywords: ['market', 'competitor', 'sizing', 'entry', 'sector', 'research', 'demand', 'pricing analysis', 'policy'],
  reply: 'That sits with Cavalla Intelligence. We size the market from the bottom up, name the competitors and the terms they win on, and set out the entry route with the risks attached. A Market Scan is usually the place to begin.'
}, {
  line: 'growth',
  keywords: ['growth', 'go to market', 'go-to-market', 'gtm', 'sales', 'pipeline', 'revenue', 'leads', 'crm', 'campaign', 'price', 'pricing'],
  reply: 'That sits with Cavalla Growth. We set the segments, the offer and the price, then build the pipeline and the reporting behind it. A Go-to-Market Sprint is usually the place to begin.'
}, {
  line: 'ai',
  keywords: ['ai', 'artificial intelligence', 'model', 'automation', 'data', 'llm', 'machine learning', 'readiness', 'governance', 'carbon'],
  reply: 'That sits with Cavalla AI. We assess readiness across data, skills, use cases, governance and budget, then build the two use cases that move a number your board already watches. AI Readiness is usually the place to begin.'
}];
const REFUSAL = 'We are not able to give financial or legal advice, and nothing here should be treated as such. We can point you to the right service line and arrange a discovery call with our team.';
const ADVICE_TRIGGERS = ['invest', 'stock', 'shares', 'tax', 'legal', 'lawyer', 'sue', 'contract law', 'financial advice'];
const LINE_LABEL: Record<ServiceLineId, string> = {
  intelligence: 'Cavalla Intelligence',
  growth: 'Cavalla Growth',
  ai: 'Cavalla AI'
};
const MAX_INPUT = 400;
export function Concierge() {
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [routedLine, setRoutedLine] = React.useState<ServiceLineId | null>(null);
  const [messages, setMessages] = React.useState<Message[]>([{
    id: 0,
    from: 'concierge',
    text: 'Good day. Tell us what you are working on and we will point you to the right service line, or to a discovery call.'
  }]);
  const [capturing, setCapturing] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [captureError, setCaptureError] = React.useState<string | null>(null);
  const [captured, setCaptured] = React.useState(false);
  const [thinking, setThinking] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const nextId = React.useRef(1);
  const lastSent = React.useRef(0);
  React.useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, capturing]);
  const respond = (question: string) => {
    const lower = question.toLowerCase();
    if (ADVICE_TRIGGERS.some(term => lower.includes(term))) {
      return {
        text: REFUSAL,
        line: null as ServiceLineId | null
      };
    }
    const match = ROUTES.find(route => route.keywords.some(keyword => lower.includes(keyword)));
    if (!match) {
      return {
        text: 'We work across three lines: Intelligence for market questions, Growth for demand and revenue, and AI for augmented intelligence. Which of those is closest to your question?',
        line: null as ServiceLineId | null
      };
    }
    return {
      text: match.reply,
      line: match.line
    };
  };
  const reply = (text: string, line: ServiceLineId | null) => {
    if (line) setRoutedLine(line);
    setMessages(current => [...current, {
      id: nextId.current++,
      from: 'concierge',
      text,
      routeTo: line ?? undefined
    }]);
    setThinking(false);
    if (line) setCapturing(true);
  };
  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = input.trim().slice(0, MAX_INPUT);
    if (!trimmed) return;
    const now = Date.now();
    if (now - lastSent.current < 1200) return;
    lastSent.current = now;
    const visitorMessage: Message = {
      id: nextId.current++,
      from: 'visitor',
      text: trimmed
    };
    setMessages(current => [...current, visitorMessage]);
    setInput('');
    setThinking(true);

    /**
     * POST /api/concierge
     * The Claude call runs server side behind our own route, with the brand
     * system prompt, the routing rules, the refusal rules for financial and
     * legal advice, rate limiting and length caps. The key never reaches the
     * browser. If the route is unavailable we fall back to local routing rather
     * than leaving the visitor without an answer.
     */
    if (LIVE_API) {
      try {
        const history = messages.filter(message => message.from === 'visitor' || message.from === 'concierge').slice(-6).map(message => ({
          role: message.from === 'visitor' ? 'user' : 'assistant',
          content: message.text
        }));
        const result = await apiPost<{
          text: string;
          line: ServiceLineId | null;
        }>(ENDPOINTS.concierge, {
          message: trimmed,
          history
        }, {
          timeoutMs: 20000
        });
        reply(result.text, result.line ?? null);
        return;
      } catch {
        /* fall through to the local routing rules */
      }
    }
    window.setTimeout(() => {
      const {
        text,
        line
      } = respond(trimmed);
      reply(text, line);
    }, LIVE_API ? 0 : 550);
  };
  const capture = async (event: React.FormEvent) => {
    event.preventDefault();
    const invalid = validateEmail(email);
    if (invalid) {
      setCaptureError(invalid);
      return;
    }
    setCaptureError(null);
    const result = await submitLead({
      email,
      consent: true,
      source: 'ai-concierge',
      line: routedLine ?? undefined
    });
    if (!result.ok) {
      setCaptureError(result.error ?? 'Something went wrong. Please try again.');
      return;
    }
    setCaptured(true);
    setCapturing(false);
    setMessages(current => [...current, {
      id: nextId.current++,
      from: 'concierge',
      text: 'Thank you. We have your enquiry and a member of the team will reply within one working day. You can also book a discovery call now.'
    }]);
  };
  return <>
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls="concierge-panel" className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gold text-charcoal shadow-lg transition-colors hover:bg-[#ff7440]" data-id="element-699" data-mp="362n5a2myew9z">
        {open ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-700" data-mp="3k8orrzmdjvfj" /> : <MessageSquareIcon className="h-5 w-5" aria-hidden="true" data-id="element-701" data-mp="3g7d0lpnur258" />}
        <span className="sr-only" data-id="element-702" data-mp="3g58z1edfef84">{open ? 'Close the Cavalla concierge' : 'Open the Cavalla concierge'}</span>
      </button>

      <AnimatePresence data-id="element-703" data-mp="bsvc1mzvanzj">
        {open && <motion.div id="concierge-panel" role="dialog" aria-label="Cavalla concierge" initial={reduced ? false : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduced ? undefined : {
        opacity: 0,
        y: 12
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="fixed bottom-24 right-5 z-40 flex max-h-[70vh] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-md border border-line bg-white shadow-2xl" data-id="element-704" data-mp="brtwp6btjfc0">
            <div className="surface-dark px-5 py-4" data-id="element-705" data-mp="mgga1ai4jakz">
              <p className="font-display text-[17px] text-white" data-id="element-706" data-mp="mfn94q5ujfar">Cavalla concierge</p>
              <p className="mt-1 text-small text-white/60" data-id="element-707" data-mp="rgkqx1my27bq">
                Made for people, powered by AI. We do not give financial or legal advice.
              </p>
            </div>

            <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-id="element-708" data-mp="hgk6fcdc9l5y">
              {messages.map(message => <div key={message.id} className={message.from === 'visitor' ? 'flex justify-end' : 'flex justify-start'} data-id="element-709" data-mp="157g4uscavem6">
                  <div className={`max-w-[85%] rounded-md px-3.5 py-2.5 text-[14px] leading-relaxed ${message.from === 'visitor' ? 'bg-royal text-charcoal' : 'bg-mist text-body'}`} data-id="element-710" data-mp="vg3ek565gwi9">
                    {message.text}
                    {message.routeTo && message.routeTo !== 'call' && <Link to={`/services/${message.routeTo}`} className="mt-3 block text-[13px] font-medium text-royal underline underline-offset-4" data-id="element-711" data-mp="3dlcwqf5t86l0">
                        Read about {LINE_LABEL[message.routeTo]}
                      </Link>}
                  </div>
                </div>)}
              {thinking && <p className="text-small text-muted" data-id="element-712" data-mp="2uobmofxefga7">Composing a reply</p>}

              {capturing && !captured && <form onSubmit={capture} className="rounded-md border border-line bg-champagne p-4" data-id="element-713" data-mp="2prbfg1otc7hi">
                  <label htmlFor="concierge-email" className="block text-[13px] font-medium text-ink" data-id="element-714" data-mp="2zn653grgf2ts">
                    Leave your email and we will follow up
                  </label>
                  <div className="mt-2 flex gap-2" data-id="element-715" data-mp="j0dgngmj8hfl">
                    <input id="concierge-email" type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" className="w-full rounded-sm border border-line bg-white px-3 py-2 text-[14px] text-ink" data-id="element-716" data-mp="evvz7o3nag7z" />
                    <button type="submit" className="shrink-0 rounded-sm bg-gold px-3 py-2 text-[13px] font-medium text-charcoal transition-colors hover:bg-[#ff7440]" data-id="element-717" data-mp="av0go9neapnl">
                      Send
                    </button>
                  </div>
                  {captureError && <p role="alert" className="mt-2 text-small text-[#FF8B7A]" data-id="element-718" data-mp="3o2bn8hi7ohau">
                      {captureError}
                    </p>}
                </form>}

              {captured && <Link to="/contact" className="block rounded-sm bg-gold px-4 py-2.5 text-center text-[14px] font-medium text-charcoal" data-id="element-719" data-mp="1map38zwqlmnv">
                  Book a discovery call
                </Link>}
            </div>

            <form onSubmit={send} className="border-t border-line p-3" data-id="element-720" data-mp="23kj662627zoz">
              <label htmlFor="concierge-input" className="sr-only" data-id="element-721" data-mp="23mnbmojlx6rd">
                Your question
              </label>
              <div className="flex items-center gap-2" data-id="element-722" data-mp="1avvhdwm3qs6o">
                <input id="concierge-input" value={input} maxLength={MAX_INPUT} onChange={event => setInput(event.target.value)} placeholder="Ask about a market, growth or AI" className="w-full rounded-sm border border-line px-3 py-2.5 text-[14px] text-ink" data-id="element-723" data-mp="1axgd3pk5kh64" />
                <button type="submit" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-gold text-charcoal transition-colors hover:bg-[#ff7440]" data-id="element-724" data-mp="2w86spmqw50e9">
                  <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-725" data-mp="117wiz04efeo1" />
                  <span className="sr-only" data-id="element-726" data-mp="w5xflq8nshr3">Send message</span>
                </button>
              </div>
            </form>
          </motion.div>}
      </AnimatePresence>
    </>;
}